import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, fetchGuidanceForRequirement } from "@/integrations/supabase/client";
import { StandardDetailHeader } from "@/components/standard/StandardDetailHeader";
import { StandardRequirementsTable } from "@/components/standard/StandardRequirementsTable";
import { EditRequirementDialog } from "@/components/standard/EditRequirementDialog";
import GuidanceDialog from "@/components/GuidanceDialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Requirement } from "@/types/project";
import { AppNavbar } from "@/components/layout/AppNavbar";

const StandardDetail = () => {
  const { standardId } = useParams<{ standardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [isGuidanceDialogOpen, setIsGuidanceDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [guidanceData, setGuidanceData] = useState<any>(null);
  const [isLoadingGuidance, setIsLoadingGuidance] = useState(false);
  
  const { data: standard, isLoading: isLoadingStandard } = useQuery({
    queryKey: ['standard', standardId],
    queryFn: async () => {
      if (!standardId) throw new Error("Standard ID is required");
      
      const { data, error } = await supabase
        .from('standards')
        .select('*')
        .eq('id', standardId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!standardId
  });
  
  const { data: requirements, isLoading: isLoadingRequirements } = useQuery({
    queryKey: ['standard-requirements', standardId],
    queryFn: async () => {
      if (!standardId) throw new Error("Standard ID is required");
      
      const { data, error } = await supabase
        .from('requirements')
        .select('*')
        .eq('standard_id', standardId);
      
      if (error) throw error;
      return data;
    },
    enabled: !!standardId
  });
  
  const updateRequirementMutation = useMutation({
    mutationFn: async (updatedRequirement: Requirement) => {
      const { error } = await supabase
        .from('requirements')
        .update({
          section: updatedRequirement.section,
          subsection: updatedRequirement.subsection,
          requirement_type: updatedRequirement.requirement_type || updatedRequirement.type,
          requirement_text: updatedRequirement.requirement_text || updatedRequirement.text
        })
        .eq('id', updatedRequirement.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standard-requirements', standardId] });
    }
  });
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast.success("You have been logged out");
      navigate("/login");
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message || "Logout failed");
    }
  };
  
  const handleViewGuidance = async (requirement: Requirement) => {
    setSelectedRequirement(requirement);
    setIsLoadingGuidance(true);
    
    try {
      const guidance = await fetchGuidanceForRequirement(requirement.id);
      setGuidanceData(guidance);
      setIsGuidanceDialogOpen(true);
    } catch (error) {
      console.error("Error fetching guidance:", error);
      toast.error("Failed to fetch guidance information");
    } finally {
      setIsLoadingGuidance(false);
    }
  };
  
  const handleEditRequirement = (requirement: Requirement) => {
    setSelectedRequirement(requirement);
    setIsEditDialogOpen(true);
  };
  
  const handleSaveRequirement = async (updatedRequirement: Requirement) => {
    await updateRequirementMutation.mutateAsync(updatedRequirement);
  };
  
  if (isLoadingStandard) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading standard details...</p>
      </div>
    );
  }
  
  if (!standard) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <h1 className="text-2xl font-bold mb-4">Standard Not Found</h1>
        <p className="text-muted-foreground mb-6">
          The standard you're looking for doesn't exist or you don't have access to it.
        </p>
        <Button onClick={() => navigate("/standards")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Standards
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppNavbar />

      <main className="flex-1 container py-6">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/standards')}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> 
            Back to Standards
          </Button>
          <h1 className="text-2xl font-bold">Standard Details</h1>
        </div>
        
        <StandardDetailHeader 
          title={standard.title}
          category={standard.category}
          identifier={standard.identifier}
          version={standard.version}
          status={standard.status}
          scope={standard.scope}
          purpose={standard.purpose}
        />
        
        <StandardRequirementsTable 
          requirements={requirements || []}
          isLoading={isLoadingRequirements}
          onViewGuidance={handleViewGuidance}
          onEditRequirement={handleEditRequirement}
        />
      </main>
      
      <GuidanceDialog 
        open={isGuidanceDialogOpen}
        onOpenChange={setIsGuidanceDialogOpen}
        guidance={guidanceData}
        requirementText={selectedRequirement?.requirement_text || selectedRequirement?.text || ""}
      />
      
      <EditRequirementDialog 
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        requirement={selectedRequirement}
        onSave={handleSaveRequirement}
      />
    </div>
  );
};

export default StandardDetail;
